import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Users, Shield, Handshake, CheckCircle, ArrowRight, Clock, Award, Building2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const values = [
  { 
    icon: Target, 
    title: 'Industry Focus', 
    desc: 'Specialising in construction, engineering and industrial recruitment means we truly understand your sector.' 
  },
  { 
    icon: Shield, 
    title: 'Quality First', 
    desc: 'Every candidate is thoroughly screened. We prioritise quality matches over high volume.' 
  },
  { 
    icon: Handshake, 
    title: 'Partnership Approach', 
    desc: 'We work as an extension of your team, invested in your long-term hiring success.' 
  },
  { 
    icon: Users, 
    title: 'People-Centred', 
    desc: 'Great recruitment starts with understanding people – both employers and candidates.' 
  },
];

const impactStats = [
  { number: '10+', label: 'Years Experience', icon: Clock },
  { number: '500+', label: 'Placements Made', icon: Users },
  { number: '100+', label: 'Client Partners', icon: Building2 },
  { number: '95%', label: 'Client Retention', icon: TrendingUp },
];

const whyWorkWithUs = [
  'Deep knowledge of construction and engineering hiring',
  'Responsive communication and fast turnaround',
  'Thorough candidate screening and vetting',
  'Flexible solutions for permanent and contract roles',
  'Long-term partnership focus, not transactional relationships',
  'Understanding of urgent and project-based hiring needs',
];

const founderExperience = [
  'Over 10 years in recruitment and industry-facing roles',
  'Hands-on experience in construction and engineering environments',
  'Deep understanding of technical and project-based hiring demands',
  'Built networks across Melbourne, Victoria and Australia',
  'Track record of successful placements in blue-collar and professional roles',
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO page="about" />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">About Vulcan Recruitment</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Specialist Recruitment For The Industries That Build Australia
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Built on more than 10 years of recruitment and industry-facing experience, Vulcan Recruitment was created to solve a simple problem: too many employers waste time on poor-fit candidates, slow hiring processes, and recruiters who don't truly understand the demands of technical and project-based environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-[#1A8B9D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-white/80 mx-auto mb-2" />
                <p className="font-heading text-4xl md:text-5xl font-bold text-white">{stat.number}</p>
                <p className="text-teal-100 text-sm uppercase tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Story</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-6">
                Built To Solve Real Hiring Problems
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Vulcan Recruitment is an Australian recruitment business focused on construction, engineering, and technical hiring. The business was founded with a practical, relationship-driven approach to solve the challenges that too many employers face: poor-fit candidates, slow processes, and recruiters who lack real industry understanding.
                </p>
                <p>
                  The goal is not just to fill vacancies, but to connect businesses with reliable, capable people who can perform on site, in the office, and across critical project functions. We understand that in construction and engineering, every hire matters.
                </p>
                <p>
                  The wrong hire costs time, money, productivity, and momentum. The right hire can strengthen teams, improve delivery, and support long-term growth. That's why we take a different approach.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800"
                alt="Construction site"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 border border-slate-200">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#1A8B9D] rounded-full flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 uppercase">10+ Years Experience</h3>
                    <p className="text-slate-500">Recruitment & Industry Expertise</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {founderExperience.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#1A8B9D] flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Leadership</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-6">
                Founded On Industry Experience
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Vulcan Recruitment was founded by a professional with more than 10 years of recruitment and industry-facing experience. This background provides a deep understanding of what employers actually need and what makes candidates succeed in technical and project-based environments.
                </p>
                <p>
                  Having worked across both recruitment and industry roles, the founder understands the challenges on both sides of the hiring process. This dual perspective shapes how Vulcan Recruitment operates: with empathy, urgency, and a commitment to practical outcomes.
                </p>
                <p>
                  The business was built not just to be another recruitment agency, but to be a genuine partner that employers and candidates can trust.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">What We Deliver</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-6">
              Better Candidates, Better Outcomes
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              From entry-level and blue-collar positions through to technical, supervisory, and professional roles, Vulcan Recruitment works to deliver candidates who are better matched, better screened, and more aligned with each client's real needs.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 border-t-4 border-[#1A8B9D]"
            >
              <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-4">Better Matched</h3>
              <p className="text-slate-600">
                We take time to understand your specific requirements, team dynamics, and project demands. No generic CV spam – only candidates who genuinely fit.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-50 p-8 border-t-4 border-[#F5A623]"
            >
              <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-4">Better Screened</h3>
              <p className="text-slate-600">
                Every candidate goes through thorough vetting including skills assessment, reference checks, and verification. Quality control at every step.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-slate-50 p-8 border-t-4 border-[#1A8B9D]"
            >
              <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-4">Better Aligned</h3>
              <p className="text-slate-600">
                We focus on long-term fit, not just immediate availability. Candidates who align with your culture and can grow with your business.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Vision & Impact */}
      <section className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Vision</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
                Making A Lasting Impact
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  As the business grows, Vulcan Recruitment aims to make a lasting impact by improving hiring outcomes for employers and creating genuine career opportunities for candidates across Australia.
                </p>
                <p>
                  The vision is to become a trusted recruitment partner known for speed, honesty, quality, and a deep understanding of construction and engineering markets.
                </p>
                <p>
                  We believe that better recruitment creates better workplaces, stronger projects, and more successful businesses. That's the impact we're working to achieve – one placement at a time.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur p-6 border-l-4 border-[#F5A623]"
              >
                <h4 className="font-heading text-lg font-bold text-white uppercase mb-2">For Employers</h4>
                <p className="text-slate-300 text-sm">Reducing hiring friction, improving candidate quality, and supporting business growth through better workforce solutions.</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur p-6 border-l-4 border-[#1A8B9D]"
              >
                <h4 className="font-heading text-lg font-bold text-white uppercase mb-2">For Candidates</h4>
                <p className="text-slate-300 text-sm">Creating genuine career opportunities, providing honest guidance, and connecting skilled professionals with employers who value them.</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur p-6 border-l-4 border-[#F5A623]"
              >
                <h4 className="font-heading text-lg font-bold text-white uppercase mb-2">For Industry</h4>
                <p className="text-slate-300 text-sm">Contributing to stronger construction and engineering sectors by ensuring the right people are in the right roles.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Values</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase">
              What Drives Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-8 border border-slate-200 hover:border-[#1A8B9D]/50 transition-colors"
              >
                <value.icon className="w-10 h-10 text-[#1A8B9D] mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 md:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Why Work With Us</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
                A Different Kind Of Recruitment Experience
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We're not a generalist agency trying to fill any role in any industry. We're specialists who understand your sector and can deliver candidates that actually fit.
              </p>
              <Link to="/employers#request-staff">
                <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide">
                  Partner With Us <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-4">
                {whyWorkWithUs.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 bg-slate-800/50 p-4">
                    <CheckCircle className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                    <p className="text-slate-300">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-6">
              Ready To Work With Us?
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Whether you're looking to hire or seeking your next opportunity, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/employers">
                <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide px-8 py-4">
                  For Employers
                </Button>
              </Link>
              <Link to="/candidates">
                <Button variant="outline" className="border-2 border-[#1A8B9D] text-[#1A8B9D] hover:bg-[#1A8B9D] hover:text-white font-bold uppercase tracking-wide px-8 py-4">
                  For Candidates
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
